import { cva } from "class-variance-authority";

/**
 * Botão do Design System Fidexa.
 *
 * Contraste: a variante sólida usa `ds-brand-600` (#C04800), não o `ds-brand-500`
 * da marca — branco sobre #E85B03 dá 3.43:1 e reprova em WCAG AA para texto.
 *
 * Toque: altura mínima 44px em todos os tamanhos (`sm` inclusive), porque o
 * alvo de toque não pode encolher só porque o botão é secundário.
 */
export const buttonVariants = cva(
  [
    "group inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-ds-sans font-medium cursor-pointer select-none",
    "rounded-ds-md border border-transparent",
    "transition-[background-color,border-color,color,box-shadow] duration-150 ease-ds-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ds-paper",
    "disabled:cursor-not-allowed disabled:opacity-55",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "motion-reduce:transition-none",
  ],
  {
    variants: {
      variant: {
        /** Ação principal da tela. No máximo uma por visão. */
        primary:
          "bg-ds-brand-600 text-ds-on-solid hover:bg-ds-brand-700 active:bg-ds-brand-800 disabled:hover:bg-ds-brand-600",
        /** Ação secundária ao lado de uma primária. */
        secondary:
          "bg-ds-surface text-ds-ink-900 border-ds-border-strong hover:bg-ds-ink-50 active:bg-ds-ink-100 disabled:hover:bg-ds-surface",
        /** Terciária: ação de baixa ênfase, sem peso visual. */
        ghost:
          "bg-transparent text-ds-ink-700 hover:bg-ds-ink-100 active:bg-ds-ink-200 disabled:hover:bg-transparent",
        /** Destrutiva. Sempre com confirmação antes de executar. */
        danger:
          "bg-ds-danger-700 text-ds-on-solid hover:brightness-90 active:brightness-75 disabled:hover:brightness-100",
      },
      size: {
        sm: "min-h-11 px-3 text-ds-small [&_svg]:size-4",
        md: "min-h-12 px-4 text-ds-body [&_svg]:size-5",
        lg: "min-h-14 px-6 text-ds-lead [&_svg]:size-5",
      },
      fullWidth: {
        true: "w-full",
        false: "w-full sm:w-auto",
      },
      /**
       * `circle` é o botão só de ícone: quadrado perfeito, redondo e sem
       * padding lateral. Nasceu de quatro cópias na mão no Backoffice
       * (filtrar, adicionar, clonar, excluir), cada uma com um tamanho — e
       * três delas ovaladas, porque fixavam a largura e deixavam a altura
       * mínima do tamanho `md` valendo.
       *
       * Ele **exige `aria-label`**: sem rótulo visível, o ícone sozinho não
       * diz nada a quem escuta a página.
       */
      shape: {
        rect: "",
        circle: "shrink-0 rounded-full px-0",
      },
    },
    compoundVariants: [
      // A largura precisa casar com a altura mínima de cada tamanho, e vencer
      // o `w-full sm:w-auto` do fullWidth — por isso vem aqui, depois dele.
      { shape: "circle", size: "sm", className: "size-11 w-11 sm:w-11" },
      { shape: "circle", size: "md", className: "size-12 w-12 sm:w-12" },
      { shape: "circle", size: "lg", className: "size-14 w-14 sm:w-14" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
      shape: "rect",
    },
  }
);
